import { useMemo, useState } from "react";
import H2Base from "../../../components/ui/titles/H2Base";

const FLAT_TAX = 0.314;

const formatEuro = (value) => `${Math.round(value).toLocaleString("fr-FR")} €`;
const formatDeduction = (value) => `− ${formatEuro(value)}`;

/**
 * Interets bruts percus par l'investisseur sur toute la duree du pret.
 *
 * Credit amortissable : l'emprunteur verse une mensualite constante couvrant
 * les interets et une part de capital. Le capital restant du diminue chaque
 * mois, donc les interets percus aussi.
 *   mensualite = P x r / (1 - (1 + r)^-n)
 *   interets   = mensualite x n - P
 *
 * Credit in fine : l'emprunteur ne verse que les interets chaque mois et
 * rembourse la totalite du capital a l'echeance. Le capital restant du reste
 * donc constant, et les interets avec lui.
 *   interets = P x r x n
 *
 * A capital, taux et duree identiques, le in fine rapporte davantage puisque
 * la totalite du capital reste prete jusqu'au terme.
 */
const grossInterest = (principal, annualRatePct, months, type) => {
    const monthlyRate = annualRatePct / 100 / 12;
    if (monthlyRate === 0) return 0;

    if (type === "infine") {
        return principal * monthlyRate * months;
    }

    const payment = (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
    return payment * months - principal;
};

const loanTypes = [
    { value: "amortissable", label: "Crédit amortissable" },
    { value: "infine", label: "Crédit in fine" },
];

const InvestmentSimulator = () => {
    const [loanType, setLoanType] = useState("amortissable");
    const [amount, setAmount] = useState(1000);
    const [duration, setDuration] = useState(24);
    const [rate, setRate] = useState(9.5);

    const results = useMemo(() => {
        // La flat tax porte sur les interets encaisses, que l'IFU declare bruts.
        // Le cout du risque est volontairement exclu de ce calcul : l'estimation
        // affichee est donc « hors cout du risque ».
        const gross = grossInterest(amount, rate, duration, loanType);
        const tax = gross * FLAT_TAX;

        return { gross, tax, net: gross - tax };
    }, [amount, duration, rate, loanType]);

    return (
        <section className="py-16 md:py-20">
            <div className="max-w-2xl">
                <H2Base text="Simulez votre investissement" tailwindClass="text-2xl md:text-3xl" />
                <p className="leading-relaxed text-gray-600">
                    Un calcul en direct des intérêts perçus sur toute la durée du prêt — coût du risque et
                    fiscalité compris.
                </p>
            </div>

            <div className="mt-10 grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-200 shadow-xl lg:grid-cols-2">
                <div className="border-b border-gray-200 bg-white p-8 lg:border-b-0 lg:border-r">
                    <div className="mb-7">
                        <p className="mb-2.5 text-sm font-semibold text-secondaryDark">Type de prêt</p>
                        <div className="flex gap-2">
                            {loanTypes.map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setLoanType(value)}
                                    className={`flex-1 rounded-lg border px-3 py-2.5 text-xs font-semibold transition ${
                                        loanType === value
                                            ? "border-secondary bg-secondary text-white"
                                            : "border-gray-200 bg-white text-gray-600 hover:border-secondaryLight"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-7">
                        <label htmlFor="sim-amount" className="mb-2.5 flex items-baseline justify-between text-sm font-semibold text-secondaryDark">
                            Montant investi
                            <span className="text-base font-bold text-secondary">{formatEuro(amount)}</span>
                        </label>
                        <input
                            id="sim-amount"
                            type="range"
                            min="20"
                            max="10000"
                            step="20"
                            value={amount}
                            onChange={(event) => setAmount(Number(event.target.value))}
                            className="h-1 w-full accent-secondary"
                        />
                    </div>

                    <div className="mb-7">
                        <label htmlFor="sim-duration" className="mb-2.5 flex items-baseline justify-between text-sm font-semibold text-secondaryDark">
                            Durée du prêt
                            <span className="text-base font-bold text-secondary">{duration} mois</span>
                        </label>
                        <input
                            id="sim-duration"
                            type="range"
                            min="6"
                            max="60"
                            step="6"
                            value={duration}
                            onChange={(event) => setDuration(Number(event.target.value))}
                            className="h-1 w-full accent-secondary"
                        />
                    </div>

                    <div>
                        <label htmlFor="sim-rate" className="mb-2.5 flex items-baseline justify-between text-sm font-semibold text-secondaryDark">
                            Taux d'intérêt annuel
                            <span className="text-base font-bold text-secondary">
                                {rate.toFixed(2).replace(".", ",")} %
                            </span>
                        </label>
                        <input
                            id="sim-rate"
                            type="range"
                            min="6"
                            max="12"
                            step="0.05"
                            value={rate}
                            onChange={(event) => setRate(Number(event.target.value))}
                            className="h-1 w-full accent-secondary"
                        />
                    </div>
                </div>

                <div className="bg-secondaryDark p-8 text-white">
                    <div className="flex items-baseline justify-between border-b border-white/10 py-3.5">
                        <span className="text-sm text-secondaryExtraLight">Intérêts bruts estimés</span>
                        <span className="ml-4 flex-shrink-0 text-lg font-bold">{formatEuro(results.gross)}</span>
                    </div>
                    <div className="flex items-baseline justify-between border-b border-white/10 py-3.5">
                        <span className="text-sm text-secondaryExtraLight">
                            Flat Tax (CSG, CRDS de 31,4 %) pour investisseurs français
                        </span>
                        <span className="ml-4 flex-shrink-0 text-lg font-bold">{formatDeduction(results.tax)}</span>
                    </div>
                    <div className="flex items-baseline justify-between pt-5">
                        <span className="text-sm font-semibold text-accent">
                            Estimation nette totale (hors coût du risque)
                        </span>
                        <span className="ml-4 flex-shrink-0 text-2xl font-bold text-accent">
                            {formatEuro(results.net)}
                        </span>
                    </div>

                    <p className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4 text-[11px] leading-relaxed text-gray-400">
                        Simulation théorique. En crédit amortissable, l'emprunteur verse une mensualité constante :
                        le capital restant dû diminue chaque mois, et les intérêts perçus avec lui. En crédit in
                        fine, seuls les intérêts sont versés mensuellement et le capital est remboursé à
                        l'échéance, ce qui maintient les intérêts à leur niveau maximal sur toute la durée. Les
                        échéances encaissées ne sont pas supposées réinvesties dans cette simulation. La Flat Tax
                        (CSG, CRDS..) en vigueur en 2026 s'applique aux intérêts encaissés, hors situation fiscale
                        personnelle. Le coût du risque moyen constaté par PretUp sur 10 ans (1,95 % par an depuis
                        2015) n'a pas été pris en compte dans cette simulation. Une éventuelle déduction fiscale
                        des pertes en capital n'est pas prise en compte. Un investissement en financement
                        participatif comporte un risque de perte en capital. Les performances passées ne préjugent
                        pas des performances futures
                    </p>
                </div>
            </div>
        </section>
    );
};

export default InvestmentSimulator;
